import { PartialRealisationAllocationLogic } from 'further-types/lp/realisation';
import { useEffect } from 'react';
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { realisation as rules } from 'further-ui/validations';
import { CalendarDay } from 'further-ui/utils';
import { skipToken } from '@tanstack/react-query';
import api from 'lib/trpcClient';
import { useNotification } from 'hooks/ui/useNotification';

type UseUnitAllocationProps = {
  index: number;
  unitsSold: number;
  selectedPartialRealisationAllocationLogic: PartialRealisationAllocationLogic;
  assetId?: string;
  vehicleId: string;
  ledgerDate?: CalendarDay;
  setValue: UseFormSetValue<rules.CreateRealisationFormValues>;
  getValues: UseFormGetValues<rules.CreateRealisationFormValues>;
  selectedHoldingClassId?: string;
};

const useUnitAllocation = ({
  index,
  unitsSold,
  selectedPartialRealisationAllocationLogic,
  assetId,
  vehicleId,
  ledgerDate,
  setValue,
  getValues,
  selectedHoldingClassId,
}: UseUnitAllocationProps) => {
  const notification = useNotification();

  const queryInput =
    assetId &&
    selectedHoldingClassId &&
    unitsSold &&
    selectedPartialRealisationAllocationLogic !==
      PartialRealisationAllocationLogic.Manual
      ? {
          assetId,
          vehicleId,
          holdingClassId: selectedHoldingClassId,
          unitsSold,
          partialRealisationAllocationLogic:
            selectedPartialRealisationAllocationLogic,
          date: ledgerDate,
        }
      : skipToken;

  const { data, isFetching, isLoading, isError } =
    api.lpRealisation.getUnitAllocations.useQuery(queryInput, {
      gcTime: 0,
      refetchOnWindowFocus: false,
    });

  useEffect(() => {
    if (!isError) return;
    notification.error(
      'Failed to calculate unit allocations. Please try again.',
    );
    setValue(
      `holdingClassRealisation.${index}.unitsAllocationPerInvestment`,
      [],
    );
  }, [isError, notification, setValue, index]);

  useEffect(() => {
    if (
      !data ||
      selectedPartialRealisationAllocationLogic ===
        PartialRealisationAllocationLogic.Manual
    )
      return;

    setValue(`holdingClassRealisation.${index}.name`, data.holdingClassName);
    setValue(
      `holdingClassRealisation.${index}.unitsAllocationPerInvestment`,
      data.allocations,
      { shouldDirty: true },
    );
  }, [data, selectedPartialRealisationAllocationLogic, setValue, index]);

  const onManualAllocationChange = () => {
    setValue(
      `holdingClassRealisation.${index}.partialRealisationAllocationLogic`,
      PartialRealisationAllocationLogic.Manual,
    );
  };

  const unitsAllocationPerInvestment =
    getValues(
      `holdingClassRealisation.${index}.unitsAllocationPerInvestment`,
    ) ?? [];

  return {
    isLoading: isFetching || isLoading,
    onManualAllocationChange,
    unitsAllocationPerInvestment,
  };
};

export default useUnitAllocation;
